import {
	Card,
	Container,
	Loader,
	SimpleGrid,
	Text,
	Title,
} from "@mantine/core";
import {
	IconBuilding,
	IconChartBar,
	IconRobot,
	IconSettings,
	IconTerminal2,
	IconUser,
	IconUsers,
} from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { useAuthCompat } from "../../hooks/useAuthCompat";

export const Route = createFileRoute("/admin/")({
	component: AdminIndexPage,
});

const adminPages = [
	{
		title: "Organizations",
		description: "View and manage all organizations",
		href: "/admin/organizations",
		icon: IconBuilding,
	},
	{
		title: "Users",
		description: "View and manage all users",
		href: "/admin/users",
		icon: IconUser,
	},
	{
		title: "Waitlist",
		description: "Manage users waiting for beta access",
		href: "/admin/waitlist",
		icon: IconUsers,
	},
	{
		title: "AI Usage",
		description: "View AI API call usage and costs",
		href: "/admin/ai-usage",
		icon: IconRobot,
	},
	{
		title: "Statistics",
		description: "User activity tracking and DAU/WAU/MAU",
		href: "/admin/statistics",
		icon: IconChartBar,
	},
	{
		title: "Scripts",
		description: "Run admin maintenance scripts",
		href: "/admin/scripts",
		icon: IconTerminal2,
	},
];

function AdminIndexPage() {
	const { user, isLoading } = useAuthCompat();

	useEffect(() => {
		if (isLoading) return;
		if (!user) {
			window.location.href = "/welcome";
			return;
		}
		if (!user.isEmployee) {
			window.location.href = "/";
		}
	}, [user, isLoading]);

	if (isLoading || !user?.isEmployee) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Loader size="lg" />
			</div>
		);
	}

	return (
		<div className="h-[calc(100vh-var(--header-height))] overflow-auto bg-[var(--mantine-color-body)]">
			<Container size="lg" py="xl">
				<div className="flex items-center gap-4 mb-2">
					<IconSettings size={32} color="var(--mantine-color-blue-6)" />
					<Title order={1}>Admin</Title>
				</div>
				<div className="mb-8">
					<Link to="/" style={{ color: "var(--mantine-color-anchor)" }}>
						&larr; Back to app
					</Link>
				</div>

				<SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
					{adminPages.map((page) => (
						<Link
							key={page.href}
							to={page.href}
							style={{ textDecoration: "none" }}
						>
							<Card
								shadow="sm"
								padding="lg"
								radius="md"
								withBorder
								className="h-full hover:shadow-md transition-shadow cursor-pointer"
							>
								<div className="flex items-center gap-3 mb-3">
									<page.icon size={24} color="var(--mantine-color-blue-6)" />
									<Text fw={500} size="lg">
										{page.title}
									</Text>
								</div>
								<Text size="sm" c="dimmed">
									{page.description}
								</Text>
							</Card>
						</Link>
					))}
				</SimpleGrid>
			</Container>
		</div>
	);
}
